import React from 'react';
import PropTypes from 'prop-types';
import { useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import styled from 'styled-components';
import { useLanguage } from '../context/language';

const StyledLanguageSwitcher = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--surface-border);
  border-radius: 999px;
  background: var(--surface);
  overflow: hidden;

  button {
    border: 0;
    background: transparent;
    color: var(--light-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    line-height: 1;
    padding: 8px 12px;
    transition: var(--transition);

    &:hover,
    &:focus-visible {
      color: var(--lightest-slate);
      background: rgba(227, 237, 249, 0.12);
      outline: 0;
    }

    &.active {
      color: var(--lightest-slate);
      background: rgba(227, 237, 249, 0.14);
    }
  }
`;

const normalizePathname = pathname => {
  if (!pathname) {
    return '/';
  }

  if (pathname === '/') {
    return '/';
  }

  return pathname.replace(/\/+$/, '');
};

const getLocalizedPath = (pathname, nextLanguage) => {
  const normalizedPath = normalizePathname(pathname);

  if (normalizedPath === '/' || normalizedPath === '/en' || normalizedPath === '/fr') {
    return `/${nextLanguage}`;
  }

  if (normalizedPath.startsWith('/en/')) {
    return normalizedPath.replace(/^\/en(?=\/)/, `/${nextLanguage}`);
  }

  if (normalizedPath.startsWith('/fr/')) {
    return normalizedPath.replace(/^\/fr(?=\/)/, `/${nextLanguage}`);
  }

  return `/${nextLanguage}`;
};

const LanguageSwitcher = ({ onSelect }) => {
  const { language, setLanguage, content } = useLanguage();
  const location = useLocation();

  const handleSelect = value => {
    setLanguage(value);

    const targetPath = getLocalizedPath(location.pathname, value);
    const currentPath = normalizePathname(location.pathname);
    const currentUrl = `${currentPath}${location.hash || ''}`;
    const targetUrl = `${targetPath}${location.hash || ''}`;

    if (targetUrl !== currentUrl) {
      navigate(targetUrl);
    }

    if (onSelect) {
      onSelect(value);
    }
  };

  return (
    <StyledLanguageSwitcher role="group" aria-label={content.nav.languageSwitcherAriaLabel}>
      <button
        type="button"
        className={language === 'en' ? 'active' : ''}
        onClick={() => handleSelect('en')}>
        EN
      </button>

      <button
        type="button"
        className={language === 'fr' ? 'active' : ''}
        onClick={() => handleSelect('fr')}>
        FR
      </button>
    </StyledLanguageSwitcher>
  );
};

LanguageSwitcher.propTypes = {
  onSelect: PropTypes.func,
};

LanguageSwitcher.defaultProps = {
  onSelect: null,
};

export default LanguageSwitcher;
